import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

interface RescheduleTaskDialogProps {
  open: boolean;
  /** Current scheduled date of the task (ISO 8601). Pre-fills the picker. */
  currentDate: string;
  /** Whether the parent mutation is in flight — disables the Save button. */
  isPending: boolean;
  onClose: () => void;
  onConfirm: (newDateIso: string) => void;
}

/**
 * Modal that lets the CSM pick a new date for an AM task. Constraint: minDate = today
 * (no past). No upper bound and no weekend auto-shift — the CSM picks freely and is
 * responsible for the choice. The parent owns the actual mutation.
 */
const RescheduleTaskDialog: React.FC<RescheduleTaskDialogProps> = ({
  open,
  currentDate,
  isPending,
  onClose,
  onConfirm,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs(currentDate));

  // Re-sync the picker when the dialog reopens for a different task (or the
  // same task whose date was changed in the meantime).
  useEffect(() => {
    if (open) {
      setSelectedDate(dayjs(currentDate));
    }
  }, [open, currentDate]);

  const today = dayjs().startOf("day");
  const isInPast = selectedDate !== null && selectedDate.isBefore(today);
  const isUnchanged =
    selectedDate !== null && selectedDate.isSame(dayjs(currentDate), "day");
  const canSave = selectedDate !== null && !isInPast && !isUnchanged && !isPending;

  const handleConfirm = () => {
    if (!selectedDate || !canSave) return;
    // Build the ISO string from the formatted calendar day so the picked date is
    // sent as midnight in Zulu time. `startOf("day").toISOString()` would convert
    // local-tz midnight back to Zulu and shift the date by one day in some
    // timezones — building the string explicitly avoids that.
    const isoUtcMidnight = `${selectedDate.format("YYYY-MM-DD")}T00:00:00.000Z`;
    onConfirm(isoUtcMidnight);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t("accountManagement.rescheduleDialogTitle", "Modifier la date")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            "accountManagement.rescheduleDialogHelp",
            "Choisissez la nouvelle date d'exécution de la tâche. Les dates passées ne sont pas autorisées."
          )}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
            <DatePicker
              label={t("accountManagement.rescheduleNewDate", "Nouvelle date")}
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              minDate={today}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t("accountManagement.cancel", "Annuler")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!canSave}
        >
          {t("accountManagement.save", "Enregistrer")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleTaskDialog;
